/**
 * Life4Earth V2 — React hook for loading KPI progress entries from the API.
 *
 * Replaces: localStorage reads from l4e_kpi_progress_entries
 */

import { useState, useEffect, useCallback, useRef } from "react";
import {
  fetchAllKpiProgressEntries,
  kpiProgressEntryToStoreShape,
} from "./kpiProgressEntries";
import type { KpiProgressEntryResponse } from "./kpiProgressEntries";
import {
  fetchForKey,
  invalidateAndRefetch,
  CACHE_UPDATE_EVENT,
  QUERY_KEYS,
} from "./queryCache";

export interface UseApiKpiProgressResult {
  raw: KpiProgressEntryResponse[];
  entries: Record<string, any>[];
  loading: boolean;
  error: string | null;
  source: "api" | "seed" | "loading";
  refetch: () => void;
}

export function useApiKpiProgress(): UseApiKpiProgressResult {
  const [raw, setRaw] = useState<KpiProgressEntryResponse[]>([]);
  const [entries, setEntries] = useState<Record<string, any>[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [source, setSource] = useState<"api" | "seed" | "loading">("loading");
  const mountedRef = useRef(true);

  const refetch = useCallback(() => {
    invalidateAndRefetch(QUERY_KEYS.kpiProgress);
  }, []);

  const load = useCallback(() => {
    setError(null);
    fetchForKey(
      QUERY_KEYS.kpiProgress,
      async () => {
        const apiItems = await fetchAllKpiProgressEntries();
        return { raw: apiItems, transformed: apiItems.map(kpiProgressEntryToStoreShape) };
      }
    )
      .then((result) => {
        if (!mountedRef.current) return;
        setRaw(result.raw);
        setEntries(result.transformed);
        setSource("api");
        setError(null);
        setLoading(false);
      })
      .catch((err) => {
        if (!mountedRef.current) return;
        console.warn("[useApiKpiProgress] API unreachable:", err.message);
        setError(err.message);
        setRaw([]);
        setEntries([]);
        setSource("seed");
        setLoading(false);
      });
  }, []);

  // Initial fetch
  useEffect(() => {
    mountedRef.current = true;
    load();
    return () => {
      mountedRef.current = false;
    };
  }, [load]);

  // Subscribe to cache invalidation
  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent).detail;
      if (detail?.key === QUERY_KEYS.kpiProgress) {
        load();
      }
    };
    window.addEventListener(CACHE_UPDATE_EVENT, handler);
    return () => window.removeEventListener(CACHE_UPDATE_EVENT, handler);
  }, [load]);

  return { raw, entries, loading, error, source, refetch };
}
